import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { PosCashier, CompanyPosCashiers } from '../../models/pos.model';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Company } from '../../models/company.model';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { StorageCenter } from '../../models/storage.center.model';
import { Contact } from '../../models/contact.model';
import { authToken } from '../../scripts/authentication.script';

export class PosCashierDA {
  static apiResponse: ApiResponse

  public static async getCashiers(
    id_company: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyPosCashiers: CompanyPosCashiers | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/pos.cashiers.data.json').then(response => { companyPosCashiers = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyPosCashiers!.id_company === id_company) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyPosCashiers
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getCashier(
    id_company: string,
    id_pos_cashier: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let posCashier: PosCashier | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/pos.cashier.data.json').then(response => { posCashier = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (posCashier!.id === id_pos_cashier) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: posCashier
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos/${id_pos_cashier}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postCashier(
    id_company: string,
    name: string,
    code: string,
    stock_enabled: boolean,
    storage_center_id: string | null,
    treasury_cash_account_ids: string[],
    treasury_card_id: string,
    default_cash_currency: string,
    default_customer: { id: string, name: string, type: string, uuid: string }
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        name: name,
        code: code,
        stock_enabled: stock_enabled,
        id_storage_center: stock_enabled ? storage_center_id : null,
        treasury_cash_accounts: treasury_cash_account_ids,
        treasury_card: treasury_card_id,
        default_cash_currency: default_cash_currency,
        default_customer: default_customer
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putCashier(
    id_company: string,
    id_pos_cashier: string,
    name: string,
    code: string,
    stock_enabled: boolean,
    storage_center_id: string | null,
    treasury_cash_account_ids: string[],
    treasury_card_id: string,
    default_cash_currency: string,
    default_customer: { id: string, name: string, type: string, uuid: string }
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let contacts: Contact[] | null = null
      let treasuryAccounts: TreasuryAccount[] | null = null
      let storageCenters: StorageCenter[] | null = null
      let posCashier: PosCashier | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/contacts.data.json').then(response => { contacts = response.data.contacts }).catch(error => { alert(error) })
      await axios.get('../../../../../database/treasury.accounts.data.json').then(response => { treasuryAccounts = response.data.treasury_accounts }).catch(error => { alert(error) })
      await axios.get('../../../../../database/storage.centers.data.json').then(response => { storageCenters = response.data.storage_centers }).catch(error => { alert(error) })
      await axios.get('../../../../../database/pos.cashier.data.json').then(response => { posCashier = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (posCashier!.id === id_pos_cashier) {
        posCashier!.name = name
        posCashier!.code = code
        posCashier!.stock_enabled = stock_enabled
        posCashier!.storage_center = stock_enabled && storage_center_id ? storageCenters!.find(item => item.id === storage_center_id) as StorageCenter : null
        posCashier!.treasury_cash_accounts = treasury_cash_account_ids.map(id => treasuryAccounts!.find(item => item.id === id) as TreasuryAccount).filter(Boolean)
        posCashier!.treasury_card = treasuryAccounts!.find(item => item.id === treasury_card_id) as TreasuryAccount
        posCashier!.default_cash_currency = default_cash_currency
        ;(posCashier as any).default_customer = default_customer

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: posCashier
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos/${id_pos_cashier}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        name: name,
        code: code,
        stock_enabled: stock_enabled,
        id_storage_center: stock_enabled ? storage_center_id : null,
        treasury_cash_accounts: treasury_cash_account_ids,
        treasury_card: treasury_card_id,
        default_cash_currency: default_cash_currency,
        default_customer: default_customer
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async deleteCashier(
    id_company: string,
    id_pos_cashier: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let posCashiers: PosCashier[] | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/pos.cashiers.data.json').then(response => { posCashiers = response.data.cashiers }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (posCashiers!.some(item => item.id === id_pos_cashier)) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos/${id_pos_cashier}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.delete(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }
}
